import React, { PureComponent } from 'react';
import { Pie, Doughnut } from 'react-chartjs-2';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import './ParametersGraph.css';
import { connect } from 'react-redux';
import { getDeviceSwVersion, getDonorUnitFromBundleMembers } from 'common/utils/helpers';
import { isItBundle } from 'common/utils/deviceTwinUtils';
import { DefaultPieChartColors, Status, DEVICES_PAGE_TAB } from 'common/utils/constants';

const ParameterTables = {
  'Connection Status': (devices, aggregations) => {
    let onlineData = aggregations?.connectionState?.find((a) => a.connectionState === 'Connected');
    let offlineData = aggregations?.connectionState?.find((a) => a.connectionState === 'Disconnected');
    let onlineCount = onlineData?.count_agg ?? 0;
    let offlineCount = offlineData?.count_agg ?? 0;

    return {
      type: 'doughnut',
      data: {
        labels: ['Online', 'Offline'],
        datasets: [
          {
            data: [onlineCount, offlineCount],
            backgroundColor: DefaultPieChartColors //["#1D3648", "#C60000"]
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        reponsive: true
      }
    };
  },
  'Software Version': (devices) => {
    /* Pie chart of connected vs offline devices*/
    let versions = {};
    for (let i = 0; i < devices.length; i++) {
      let device = devices[i];
      let isBundle = isItBundle(device);

      if (isBundle) device = getDonorUnitFromBundleMembers(device.members);

      let v = getDeviceSwVersion(device);
      if (versions.hasOwnProperty(v)) {
        versions[v]++;
      } else {
        versions[v] = 1;
      }
    }

    let labels = Object.keys(versions);
    let data = labels.map((label) => {
      return { value: versions[label], label: label };
    });
    let backgroundColors = DefaultPieChartColors;

    data = data.sort((data1, data2) => {
      return data2.value - data1.value;
    });

    return {
      type: 'doughnut',
      data: {
        labels: data.map((data) => data.label),
        datasets: [
          {
            data: data.map((data) => data.value),
            backgroundColor: backgroundColors
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        reponsive: true
      }
    };
  },
  'Admin State': (devices, aggregations) => {
    let labels = aggregations?.adminState?.map((a) => a.adminState);
    let data = aggregations?.adminState?.map((a) => a.count_agg);

    let backgroundColors = DefaultPieChartColors;

    return {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: backgroundColors
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        reponsive: true
      }
    };
  },
  'Last Reset Reason': (devices, aggregations) => {
    let labels = aggregations?.lastResetReason?.map((a) => a.lastResetReason);
    let data = aggregations?.lastResetReason?.map((a) => a.count_agg);
    let backgroundColors = DefaultPieChartColors;

    return {
      type: 'doughnut',
      data: {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: backgroundColors
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        reponsive: true
      }
    };
  }
};

export const ChartLegend = (props) => {
  if (props.items == null) {
    return <div />;
  }

  let items = props.items;

  return (
    <PerfectScrollbar suppressscrollx={'true'}>
      <div className={'ChartLegend'}>
        {items.map((item, i) => {
          return (
            <div className={'ChartLegend_Item'} key={i}>
              <div className={'ChartLegend_Item_Box'} style={{ backgroundColor: item.fillStyle }} />
              <div className={'ChartLegend_Item_Text'}>{item.text ? item.text : 'undefined'}</div>
            </div>
          );
        })}
      </div>
    </PerfectScrollbar>
  );
};

class ParametersGraph extends PureComponent {
  state = {
    legendItems: null
  };

  constructor(props) {
    super(props);
    this.chartRef = React.createRef();
  }

  refreshLegend() {
    let legendItems;
    try {
      legendItems = this.chartRef.current.chartInstance.legend.legendItems;
    } catch (error) {
      legendItems = null;
    }
    if (JSON.stringify(legendItems) !== JSON.stringify(this.state.legendItems)) {
      this.setState({ legendItems: legendItems });
    }
  }

  componentDidMount() {
    this.refreshLegend();
  }

  componentDidUpdate() {
    this.refreshLegend();
  }

  render() {
    let parameter;
    this.tableInfo = null;

    if (this.props.aggregations == null || this.props.aggregations.length < 1) {
      return (
        <div className="parametersGraph_StatusBox">
          {this.props.status === Status.PENDING && (
            <svg className="spinner" viewBox="0 0 50 50">
              <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
            </svg>
          )}
          {this.props.status === Status.FAILURE && (
            <div className="errorMessage">
              An unexpected problem occurred and this data is unavailable. Please try refreshing the page, and if the
              problem persists, contact us at
              <a href="mailto:support@pivotalcommware.com"> support@pivotalcommware.com</a>
            </div>
          )}
        </div>
      );
    }

    if (this.props.parameter != null) {
      parameter = this.props.parameter;
    } else {
      return null;
    }

    let tableInfoFun = ParameterTables[parameter];
    if (tableInfoFun == null) {
      return null;
    }

    this.tableInfo = tableInfoFun(this.props.devices, this.props.aggregations);
    let tableInfo = this.tableInfo;

    let chart;

    switch (tableInfo.type) {
      case 'pie':
        chart = <Pie data={tableInfo.data} options={tableInfo.options} />;
        break;
      case 'doughnut':
        chart = (
          <Doughnut
            ref={this.chartRef}
            data={tableInfo.data}
            options={{
              cutoutPercentage: 40,
              layout: { padding: { left: 0, right: 0 } },
              legend: { display: false, position: 'right', labels: { boxWidth: 12, padding: 0 } },
              ...tableInfo.options
            }}
          />
        );
        break;
      default:
        break;
    }

    return (
      <div className={'parametersGraph'}>
        <div className="ParametersGraph_ChartDiv">{chart}</div>
        <div className="ParametersGraph_LegendDiv">
          <ChartLegend items={this.state.legendItems} />
        </div>
      </div>
    );
  }
}

export default connect((store) => {
  let aggregations;
  let parameter;

  const activeTab = store.ui.devicesPage.activeTab;
  if (activeTab === DEVICES_PAGE_TAB.pivot) {
    aggregations = store.data.pivotDeviceSets?.parametersGraph?.aggregations;
    parameter = store.ui.devicesPage.pivotTab.selectedParameters[0];
  } else if (activeTab === DEVICES_PAGE_TAB.echo) {
    aggregations = store.data.echoDeviceSets?.parametersGraph?.aggregations;
    parameter = store.ui.devicesPage.echoTab.selectedParameters[0];
  } else {
    aggregations = store.data.allDeviceSets?.parametersGraph?.aggregations;
    parameter = store.ui.devicesPage.allDevicesTab.selectedParameters[0];
  }

  return {
    aggregations,
    parameter,
    status: store.statuses?.['searchComponentFilteredDevicesV2_parametersGraph_' + activeTab]
  };
})(ParametersGraph);
